import { useMemo } from 'react';
import { isAfter } from 'date-fns';

import { FullPageContent } from '@/components/layout/full-page-content';
import { Loading } from '@/components/ui/old/Loading';
import { useCurrentAsset } from '@/hooks/use-current-asset';

import { GetChartData } from '../api/queries/get-chart-data';
import { GetLevels } from '../api/queries/get-levels';
import { ListTops } from '../api/queries/list-tops';
import { ListUnits } from '../api/queries/list-units';
import { DataExplorerFilters } from '../components/data-explorer-filters';
import { DataExplorerResult } from '../components/data-explorer-result';
import type { RequestFilters } from '../entities/filters';
import type { Node } from '../entities/node';
import { useFilters } from '../hooks/use-filters';
import { useOnlyTop } from '../hooks/use-only-top';

export function DataExplorerPage() {
  /**
   * INFO: we call a bunch of queries to show an initial loading UI for the initial loading
   */

  const [filters] = useFilters();
  const currentAsset = useCurrentAsset();

  const getLevels = GetLevels.useQuery({ retry: 3 });
  const listTops = ListTops.useQuery({ currentAsset }, { retry: 3 });

  const listUnits = ListUnits.useQuery(
    { top: filters.top },
    { retry: 3, keepPreviousData: true },
  );

  if (getLevels.isLoading) {
    return <Loading />;
  }

  if (filters.topId && listTops.isLoading) {
    return <Loading />;
  }

  if (
    filters.unitIds &&
    listUnits.isLoading &&
    listUnits.fetchStatus !== 'idle'
  ) {
    return <Loading />;
  }

  return <DataExplorerInnerPage />;
}

function DataExplorerInnerPage() {
  const onlyTop = useOnlyTop();
  const [filters, setFilters] = useFilters();

  const requestFilters = useMemo<RequestFilters | undefined>(
    function () {
      const { chartTypes, from, to, filterNoise, top, units } = filters;

      if (isAfter(from, to)) {
        return undefined;
      }

      const nodes: Node[] = onlyTop ? (top ? [top] : []) : (units ?? []);

      if (nodes.length === 0) {
        return undefined;
      }

      return {
        nodes,
        chartTypes,
        from,
        to,
        filterNoise,
      };
    },
    [filters, onlyTop],
  );

  const requests = GetChartData.useQuery(requestFilters, { retry: 3 });

  return (
    <FullPageContent scrollable testId="data-explorer-page">
      <DataExplorerFilters
        onlyTop={onlyTop}
        value={filters}
        onChange={setFilters}
      />

      {requestFilters ? (
        <DataExplorerResult
          requestFilters={requestFilters}
          requests={requests}
          onUpdateFilters={setFilters}
        />
      ) : (
        <div className="text-md flex h-5/6 items-center justify-center font-light text-gray-400/80">
          <p>Select the filters to see the results.</p>
        </div>
      )}
    </FullPageContent>
  );
}
